import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "farmacia_token";
const USER_KEY = "farmacia_user";

const FarmaciaAuthContext = createContext(null);

function homeForUser(data) {
  if (data?.utenti_ruoli_v1 && data?.ruolo === "autista") {
    return "/farmacia/autista";
  }
  return "/farmacia";
}

function getInitialFarmaciaToken() {
  // Auto-login da Super Admin (/admin → Farmacia → Login).
  // Esempio: /farmacia?farmacia_impersonate_token=...
  const params = new URLSearchParams(window.location.search || "");
  const impToken = params.get("farmacia_impersonate_token");
  if (!impToken) return localStorage.getItem(TOKEN_KEY);

  localStorage.setItem(TOKEN_KEY, impToken);
  localStorage.removeItem(USER_KEY);

  // Rimuove il query param per evitare riapplicazioni al refresh.
  const newUrl = `${window.location.pathname || ""}${window.location.hash || ""}`;
  window.history.replaceState({}, "", newUrl);
  return impToken;
}

export function FarmaciaAuthProvider({ children }) {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => getInitialFarmaciaToken());
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    axios.get(`${API_URL}/api/farmacia/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setUser(res.data);
        localStorage.setItem(USER_KEY, JSON.stringify(res.data));
        // Se sessione autista su route non consentita, reindirizza
        if (res.data?.utenti_ruoli_v1 && res.data?.ruolo === "autista") {
          const path = window.location.pathname || "";
          if (!path.startsWith("/farmacia/autista")) {
            navigate("/farmacia/autista", { replace: true });
          }
        }
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/farmacia/auth/login`, { email, password });
    const farm = data.farmacia || {};
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(farm));
    setToken(data.token);
    setUser(farm);
    toast.success("Accesso effettuato");
    navigate(homeForUser(farm));
  }, [navigate]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    navigate("/farmacia-login");
  }, [navigate]);

  const hasPermission = useCallback((perm) => {
    if (!user?.utenti_ruoli_v1) return true;
    return (user?.permessi || []).includes(perm);
  }, [user]);

  return (
    <FarmaciaAuthContext.Provider
      value={{
        token,
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!token,
        hasPermission,
      }}
    >
      {children}
    </FarmaciaAuthContext.Provider>
  );
}

export function useFarmaciaAuth() {
  const ctx = useContext(FarmaciaAuthContext);
  if (!ctx) throw new Error("useFarmaciaAuth must be used within FarmaciaAuthProvider");
  return ctx;
}
